import { FC, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AllProjects } from "../AllTokens";
import { ChangePeriod, changePeriodToDisplayName } from "./ChangePeriod";
import { Column, columnDef, ColumnWidth } from "./columnDef";
import { TableCategoryTabHighlight } from "./components/TableCategoryTabHighlight";
import { TableOrderByArrowUp } from "./components/TableOrderByArrowUp";
import { TokenCategory } from "./TokenCategory";

const columnWidthToClasses: Record<ColumnWidth, string> = {
  [ColumnWidth.Tiny]: "w-[20px] min-w-[20px] max-w-[20px]",
  [ColumnWidth.Short]: "w-[100px] min-w-[100px] max-w-[100px]",
  [ColumnWidth.Medium]: "w-[180px] min-w-[180px] max-w-[180px]",
  [ColumnWidth.Wide]: "w-[270px] min-w-[270px] max-w-[270px]",
  [ColumnWidth.Full]: "w-[180px] lg:w-full",
};

enum OrderDirection {
  Ascending = "ASC",
  Descending = "DESC",
}

enum TableAllTab {
  All = "ALL",
}

type TableCategoryTab = TableAllTab; //TokenCategory | TableAllTab;

const tokenCategoryToDisplayName: Record<TableCategoryTab, string> = {
  [TableAllTab.All]: "All",
  // [TokenCategory.AI]: "AI",
  // [TokenCategory.Solana]: "Solana",
  // [TokenCategory.ETH]: "ETH",
  // [TokenCategory.Base]: "Base",
  // [TokenCategory.Memecoin]: "Memecoins",
};

const allTabs: TableCategoryTab[] = [
  ...Object.values(TableAllTab),
  // ...Object.values(TokenCategory),
];

export const AllTokensTable: FC<{
  allProjects: AllProjects[];
  isLoading: boolean;
  className?: string;
}> = ({ allProjects, isLoading, className }) => {
  const [activeTab, setActiveTab] = useState<TableCategoryTab>(TableAllTab.All);
  const [activePeriod, setActivePeriod] = useState(ChangePeriod["24H"]);
  const [orderBy, setOrderBy] = useState(Column.MarketCap);
  const [orderDirection, setOrderDirection] = useState(
    OrderDirection.Descending,
  );

  const tokens = useMemo(
    () =>
      allProjects
        .map((projectData) => {
          return {
            id: projectData.project.id,
            name: projectData.project.name,
            symbol: projectData.project.symbol || "-",
            logoSrc:
              `https://mygateway.io/assets/img/tokens/${projectData.project.symbol}.webp` ||
              undefined,
            categories: [TokenCategory.AI], // Default to AI category since we're in AI dashboard
            priceUSD: {
              current: projectData.price.price,
              changeRatio: {
                [ChangePeriod["1H"]]: projectData.price.priceChange1HPercentage,
                [ChangePeriod["24H"]]:
                  projectData.price.priceChange24HPercentage,
                [ChangePeriod["7D"]]: projectData.price.priceChange7DPercentage,
                [ChangePeriod["30D"]]:
                  projectData.price.priceChange30DPercentage,
              },
            },
            marketCapUSD: {
              current:
                projectData.price.price * projectData.price.circulatingSupply,
            },
            volumeUSD: {
              range: {
                [ChangePeriod["1H"]]: projectData.volume.last24HVolume,
                [ChangePeriod["24H"]]: projectData.volume.last24HVolume,
                [ChangePeriod["7D"]]: projectData.volume.last24HVolume,
                [ChangePeriod["30D"]]: projectData.volume.last24HVolume,
              },
              changeRatio: {
                [ChangePeriod["1H"]]:
                  projectData.volume.last1hVolumePercentageChange,
                [ChangePeriod["24H"]]:
                  projectData.volume.last24hVolumePercentageChange,
                [ChangePeriod["7D"]]:
                  projectData.volume.last7dVolumePercentageChange,
                [ChangePeriod["30D"]]:
                  projectData.volume.last30dVolumePercentageChange,
              },
            },
            gatewayScore: {
              current: projectData.gatewayScore.gatewayScore,
              changeRatio: {
                [ChangePeriod["1H"]]:
                  projectData.gatewayScore.gatewayScoreChange1HPercentage,
                [ChangePeriod["24H"]]:
                  projectData.gatewayScore.gatewayScoreChange24HPercentage,
                [ChangePeriod["7D"]]:
                  projectData.gatewayScore.gatewayScoreChange7DPercentage,
                [ChangePeriod["30D"]]:
                  projectData.gatewayScore.gatewayScoreChange30DPercentage,
              },
            },
          };
        })
        .filter(
          (token) =>
            activeTab === TableAllTab.All ||
            token.categories.includes(activeTab),
        )
        .sort((a, b) => {
          if (!columnDef[orderBy].orderByEnabled) {
            return 0;
          }

          // any because use old TypeScript version that can't infer orderByToDataField based on orderByEnabled
          const orderByToDataField = (columnDef[orderBy] as any)
            .orderByToDataField;

          const aField = orderByToDataField({ entry: a, period: activePeriod });
          const bField = orderByToDataField({ entry: b, period: activePeriod });

          switch (orderDirection) {
            case OrderDirection.Ascending: {
              return aField > bField ? 1 : -1;
            }
            case OrderDirection.Descending: {
              return aField > bField ? -1 : 1;
            }
            default: {
              return 0;
            }
          }
        }),
    [allProjects, activeTab, orderBy, activePeriod, orderDirection],
  );

  return (
    <div className={`w-full ${className || ""}`}>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between w-full mb-6 gap-4">
        <div className="grid grid-flow-col auto-cols-fr">
          {allTabs.map((tab) => (
            <button
              key={tab}
              aria-label={`Select ${tokenCategoryToDisplayName[tab]} tokens`}
              className={`relative flex flex-col font-bold text-base ${activeTab === tab ? "text-white" : "text-[rgba(255,255,255,0.5)]"}`}
              onClick={() => setActiveTab(tab)}
            >
              {tokenCategoryToDisplayName[tab]}
              {activeTab === tab && <TableCategoryTabHighlight />}
            </button>
          ))}
        </div>
        <div className="flex items-center overflow-x-auto gap-[10px]">
          {Object.values(ChangePeriod).map((period) => (
            <button
              key={period}
              aria-label={`Set data period to ${changePeriodToDisplayName[period]}`}
              className={`flex items-center justify-center w-[50px] h-[30px] font-bold text-base rounded-md ${period === activePeriod ? "text-white bg-[#007DFF]" : "text-[rgba(255,255,255,0.5)]"}`}
              onClick={() => setActivePeriod(period)}
            >
              {changePeriodToDisplayName[period]}
            </button>
          ))}
        </div>
      </div>
      <div className="relative overflow-auto">
        <div className="w-[1300px] lg:w-full rounded-2xl bg-[rgba(9,20,30,0.9)]">
          <div className="sticky top-0 flex items-center w-full py-[16px] pt-[30px] px-[30px] bg-[rgba(9,20,30,0.9)]">
            {Object.values(Column).map((column) => (
              <div
                key={column}
                className={`font-normal ${
                  column === Column.Name
                    ? `sticky left-0 z-10 bg-[#08141e]`
                    : ""
                } text-sm text-[rgba(255,255,255,0.5)] ${columnWidthToClasses[columnDef[column].width]}`}
              >
                {columnDef[column].orderByEnabled ? (
                  <button
                    aria-label={`Sort tokens by ${columnDef[column].displayHeader(activePeriod)}`}
                    className="flex items-center whitespace-nowrap"
                    onClick={() => {
                      if (orderBy !== column) {
                        setOrderBy(column);
                        setOrderDirection(OrderDirection.Descending);
                        return;
                      }

                      switch (orderDirection) {
                        case "ASC": {
                          setOrderDirection(OrderDirection.Descending);
                          break;
                        }
                        case "DESC": {
                          setOrderDirection(OrderDirection.Ascending);
                          break;
                        }
                      }
                    }}
                  >
                    {orderBy === column ? (
                      <>
                        <TableOrderByArrowUp
                          className={`w-6 h-6 min-w-6 min-h-6 max-w-6 max-h-6 ${orderDirection === OrderDirection.Descending ? "rotate-180" : ""}`}
                        />
                      </>
                    ) : (
                      <div className="w-6 h-6 min-w-6 min-h-6 max-w-6 max-h-6"></div>
                    )}

                    {columnDef[column].displayHeader(activePeriod)}
                  </button>
                ) : (
                  <div>{columnDef[column].displayHeader(activePeriod)}</div>
                )}
              </div>
            ))}
          </div>
          <div className="relative">
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div className="watermark watermark-bold"></div>
            </div>
            {tokens.map((token, index) => (
              <Link
                key={token.id}
                to={`/report/${token.id}`}
                className={`flex hover:bg-[#ffffff0a] ${index % 2 === 1 ? "bg-[#55A6FB0D]" : ""} items-center w-full py-[10px] px-[30px]`}
              >
                {Object.values(Column).map((column) => (
                  <div
                    key={column}
                    className={`pl-6 first:pl-0 ${
                      column === Column.Name ? "sticky left-0 z-10" : ""
                    } ${columnWidthToClasses[columnDef[column].width]}`}
                  >
                    {columnDef[column].displayData({
                      index: index + 1,
                      entry: token,
                      period: activePeriod,
                    })}
                  </div>
                ))}
              </Link>
            ))}
          </div>

          {isLoading && tokens.length === 0 && (
            <div className="flex justify-center p-4 w-full">
              <div className="flex justify-center items-center">
                <div className="animate-bounce inline-block h-2 w-2 bg-white rounded-full"></div>
                <div className="animate-bounce inline-block h-2 w-2 bg-white rounded-full ml-1"></div>
                <div className="animate-bounce inline-block h-2 w-2 bg-white rounded-full ml-1"></div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
